import type { CashSessionRow } from './cashSessionRow';
import type { CashPaymentMethodLine } from './cashSummaryBreakdown';

export type CashSummary = {
  activeSession: CashSessionRow | null;
  todaySalesCount: number;
  todaySalesTotal: number;
  todayCashTotal: number;
  /** Efectivo de la sesión activa (ventas con cash_session_id = activeSession.id). Solo tiene sentido cuando activeSession existe. */
  sessionCashTotal: number;
  /** Ventas del turno activo (tickets con cash_session_id = activeSession.id). */
  sessionSalesCount: number;
  sessionSalesTotal: number;
  /** Suma de `tip_amount` de ventas activas del turno (aditivo). */
  sessionTipsTotal: number;
  /** Cobros del turno por medio de pago (efectivo, QR, transferencia, customer). */
  sessionPaymentBreakdown: CashPaymentMethodLine[];
  /** Egresos activos del turno activo (cash_outflows direction='out'). */
  sessionOutflowsTotal: number;
  /** Ingresos manuales activos del turno (cash_outflows direction='in'). Aditivo; ya incluidos en sessionCashTotal. */
  sessionInflowsTotal: number;
  todayDigitalTotal: number;
  lastClosedSession: CashSessionRow | null;
  /**
   * Efectivo esperado en caja para el turno activo (server-authoritative).
   * `opening_amount + session_cash_total − session_outflows_total`
   * (session_cash_total ya incluye los ingresos manuales).
   * Null cuando no hay turno activo. Los clientes viejos sin la migración
   * pueden no recibirlo del RPC; en ese caso se computa client-side como fallback.
   */
  expectedBalance: number | null;
  /**
   * Estado de verificación remota (ADR-017: el server es la única autoridad;
   * el "local" es un stub vacío). 'ok' = el server respondió; 'unavailable' =
   * online pero el fetch remoto falló — el estado real es DESCONOCIDO, no
   * "sin turno"; 'offline' = sin conexión detectada. Ausente en resúmenes
   * armados por otros caminos (stub inicial, tests).
   */
  remoteStatus?: 'ok' | 'unavailable' | 'offline';
};

/** Efectivo esperado en caja: server `expectedBalance` cuando existe; fallback client-side. */
export function resolveExpectedCashInDrawer(summary: CashSummary | null): number | null {
  if (summary?.activeSession == null) return null;
  if (summary.expectedBalance != null) return summary.expectedBalance;
  return (
    (summary.activeSession.openingAmount ?? 0) +
    (summary.sessionCashTotal ?? 0) -
    (summary.sessionOutflowsTotal ?? 0)
  );
}
